/**
 * Polyfill for the global `structuredClone`, which does not exist on browsers
 * older than Chrome 98 / Safari 15.4 / Firefox 94. The embed is loaded on
 * partner sites where visitors may be on an outdated browser (e.g. an old
 * Android Chrome), and calling `structuredClone` there throws a ReferenceError
 * mid-render — blanking the whole experience while the host page's footer stays
 * visible. This installs a guarded fallback so those browsers keep working.
 *
 * It is not a full structured-clone algorithm: it covers the value types the
 * embed actually clones (primitives, plain objects, arrays, Dates, Maps, Sets)
 * and guards against cycles. On browsers that already ship `structuredClone`
 * the `typeof` check makes this a no-op.
 */
const cloneValue = <T>(value: T, seen: WeakMap<object, unknown>): T => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  const source = value as unknown as object;

  if (seen.has(source)) {
    return seen.get(source) as T;
  }

  if (value instanceof Date) {
    return new Date(value.getTime()) as unknown as T;
  }

  if (Array.isArray(value)) {
    const cloned: unknown[] = [];
    seen.set(source, cloned);
    value.forEach((item) => cloned.push(cloneValue(item, seen)));
    return cloned as unknown as T;
  }

  if (value instanceof Map) {
    const cloned = new Map();
    seen.set(source, cloned);
    value.forEach((entryValue, entryKey) =>
      cloned.set(cloneValue(entryKey, seen), cloneValue(entryValue, seen)),
    );
    return cloned as unknown as T;
  }

  if (value instanceof Set) {
    const cloned = new Set();
    seen.set(source, cloned);
    value.forEach((entryValue) => cloned.add(cloneValue(entryValue, seen)));
    return cloned as unknown as T;
  }

  const cloned: Record<string, unknown> = {};
  seen.set(source, cloned);
  Object.keys(value as Record<string, unknown>).forEach((key) => {
    cloned[key] = cloneValue((value as Record<string, unknown>)[key], seen);
  });
  return cloned as unknown as T;
};

if (typeof globalThis.structuredClone !== 'function') {
  globalThis.structuredClone = <T>(value: T): T =>
    cloneValue(value, new WeakMap());
}

export {};
